import { Text } from '@mantine/core'
import type { Composition, Reference } from '@medplum/fhirtypes'
import { parseDescriptionQuestionnaireUrls } from '@seen/core/models/care-plans/demographics'
import { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { Button } from '@seen/ui'
import { IconHistory, IconLink, IconSignature } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { CarePlanGoal } from '#src/components/care-plan/care-plan-goal/CarePlanGoal.tsx'
import { CarePlanActivitiesDrawer } from '#src/components/care-plan/CarePlanActivitiesDrawer.tsx'
import { CarePlanDemographicsProvider } from '#src/components/care-plan/CarePlanDemographicsContext.tsx'
import { CarePlanHeader } from '#src/components/care-plan/CarePlanHeader.tsx'
import { CarePlanPatientDetailsDisplay } from '#src/components/care-plan/CarePlanPatientDetailsDisplay.tsx'
import { CarePlanWhatMattersMost } from '#src/components/care-plan/CarePlanWhatMattersMost.tsx'
import { InterventionDrawer } from '#src/components/care-plan/interventions/InterventionDrawer.tsx'
import { useCarePlanSnapshotData } from '#src/components/care-plan/useCarePlanSnapshotData.ts'
import { Card } from '#src/components/core/Card.tsx'
import { Disclosure } from '#src/components/core/disclosure/Disclosure.tsx'
import { DisclosureContent } from '#src/components/core/disclosure/DisclosureContent.tsx'
import { DisclosureTrigger } from '#src/components/core/disclosure/DisclosureTrigger.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'

export function CarePlanSnapshotPage(): JSX.Element | null {
  const navigate = useNavigate()
  const { patientId, carePlanSnapshotId } = useParams()

  const {
    carePlan,
    goalCarePlans,
    signatures,
    interventionsByGoalCarePlan,
    isLoading,
    demographics,
  } = useCarePlanSnapshotData({
    reference: `Composition/${carePlanSnapshotId}`,
  } satisfies Reference<Composition>)

  if (isLoading) {
    return <Loader />
  }

  if (carePlan == null) {
    return (
      <Text>
        {carePlanSnapshotId == null
          ? 'There are no signed-off care plans for this participant.'
          : 'We ran into an error loading the selected care plan.'}
      </Text>
    )
  }

  return (
    <>
      <div className="grid gap-10">
        <div className="grid gap-6">
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-2">
              <CarePlanHeader
                carePlan={carePlan}
                activitiesPath="../activities"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Button variant="outline" asChild>
            <Link to={`../../${carePlan.id}`}>
              <IconLink className="mr-2 size-4" /> View Active Care Plan
            </Link>
          </Button>
          <Button onClick={() => navigate(`./signatures`)} variant="outline">
            <IconSignature className="mr-2 size-4" />{' '}
            {signatures.length === 0
              ? 'No Signatures'
              : `View Signatures ${SignatureTask.completedRatio(signatures)}`}
          </Button>
          <Button
            onClick={() => navigate(`/Patient/${patientId}/care-plans/history`)}
            variant="outline"
          >
            <IconHistory className="mr-2 size-4" /> View History
          </Button>
        </div>
        <Card className="p-0">
          <Disclosure>
            <DisclosureTrigger className="px-10 py-6">
              <h3>Participant Summary</h3>
            </DisclosureTrigger>
            <DisclosureContent className="px-10 pb-6">
              <CarePlanPatientDetailsDisplay
                patient={carePlan.patientSubject}
                goalCarePlans={goalCarePlans}
                demographics={demographics}
                readOnly={true}
                descriptionQuestionnaireUrls={parseDescriptionQuestionnaireUrls(
                  carePlan.description
                )}
              />
            </DisclosureContent>
          </Disclosure>
        </Card>
        <Card className="px-10 py-6">
          {/* Frozen (version-pinned) responses, so questionnaire nodes embedded
              in the signed description render what was signed. */}
          <CarePlanDemographicsProvider value={demographics}>
            <CarePlanWhatMattersMost carePlan={carePlan} />
          </CarePlanDemographicsProvider>
        </Card>
        {interventionsByGoalCarePlan.map(
          ({ goalCarePlan, interventions }, index) => (
            <CarePlanGoal
              key={goalCarePlan.id}
              goalNumber={index + 1}
              goalCarePlan={goalCarePlan}
              isFirst={index === 0}
              isLast={index === goalCarePlans.length - 1}
              interventions={interventions}
              allGoalCarePlans={goalCarePlans}
            />
          )
        )}
      </div>
      <CarePlanActivitiesDrawer carePlan={carePlan} />
      <InterventionDrawer
        interventionsByGoalCarePlan={interventionsByGoalCarePlan}
      />
    </>
  )
}
